import React, {
  useState, useMemo
} from 'react';
import styled, { withTheme } from 'styled-components/macro';
import { Formik, Field } from 'formik';
import * as Yup from 'yup';
import { useUserContext } from 'context/UserContext';
import { toast } from 'react-toastify';
import { Toast } from 'containers';
import { exportExcel } from 'services/helpers';
import { 
  getPivotsExcel, sendEmailAdmin, sendEmailAll, sendEmailSome, getStatsExcel
} from 'services/requests'
import queryString from 'query-string';
import cleanDeep from 'clean-deep';
import PropTypes, { func } from 'prop-types';

import {
  Layout,
} from 'components/organisms';
import {
  Title, HelperTitle, Text, Spacer, Button, Form, ButtonMenu
} from 'components/atoms';
import {
  TabsLineList, FieldCustom, FieldSelect, FieldMarkdown, Tooltip, FieldDate
} from 'components/molecules';
import LoaderSpinner from 'components/atoms/LoadingSpinner/LoadingSpinner';


// Schemas
const Schema = Yup.object().shape({
  title: Yup.string()
    .required('Campo obrigatório'),
  message: Yup.string()
    .required('Campo obrigatório'),
  to: Yup.array(),
  destination: Yup.string()
    .required('Campo obrigatório'),
});

const min = new Date();
min.setFullYear(min.getFullYear() - 1);
const max = new Date();
max.setFullYear(max.getFullYear() + 1);
const IntervalSchema = Yup.object().shape({
  startDate: Yup.date().required('Campo obrigatório'),
  endDate: Yup.date().required('Campo obrigatório').min(Yup.ref('startDate'), 'Data de fim deve ser posterior à de inicio')
});

const types = {
  all: 'all',
  some: 'some',
  admin: 'admin',
}

const functionalities = {
  exports: 'exports',
  emails: 'emails'
}

const Page = ({
  initialProps
}) => {
  const initialFunc = functionalities.exports;
  const initialTab = types.admin;
  const computedButtonModifiers = ['clean', 'primaryAlt', 'center', 'clearMargin']

  const [{ user }] = useUserContext()
  const [func, setFunc] = useState(initialFunc)
  const [tab, setTab] = useState(initialTab)
  const [valid, setValid] = useState(false)
  const [loadingStats, setLoadingStats] = useState(false)
  const [loadingPivots, setLoadingPivots] = useState(false)
  
  const [editorProps, setEditorProps] = useState(initialProps)
  const [intervalForm, setIntervalForm] = useState({
    startDate: new Date().toISOString(),
    endDate: new Date(new Date().setHours(23, 59, 59, 999)).toISOString(),
  })

  const newTabs = useMemo(() => {
    let list = [];
   
    if (user && user.admin === true) {
      list = [
        {
          onClick: () => setFunc(functionalities.exports),
          text: 'Exportação de dados',
          isActive: func === functionalities.exports,
        }
      ]
    }

    list = ([...list,
      {
        onClick: () => setFunc(functionalities.emails),
        text: 'Enviar emails',
        isActive: func === functionalities.emails,
      }
    ])

    return list;
  }, [func, user])

  const emailOptions = useMemo(() => {
    let list;
    list = [
      {
        value: 'admin',
        label: 'Administradores do Sistema',
      }
    ]
    if (user && user.admin === true) {
      list = ([...list,
        {
          value: 'all',
          label: 'Todos os pivots',
        }
      ])
    }
    if (user && user.editor === true) {
      list = ([...list,
        {
          value: 'some',
          label: 'Escolher pivots p/ Subweb',
        }
      ])
    }

    return list;
  }, [tab, user])  

  const userSubwebs = useMemo(() => {
    if (user && user.subwebs && user.subwebs.length > 0) {
      return user.subwebs.map((item) => (
        { value: item.id, label: item.slug }
      ))
    }
    return []
  }, [user])

  const handleFormChange = (newValues) => {
    let newProps = {
      ...initialProps, ...newValues
    };
    if (tab === types.all || tab === types.admin) {
      newProps = {
        ...newProps, to: []
      };
    }
    const isValid = Schema.isValidSync(newProps)
    if (isValid) {
      setEditorProps(newProps)
      setValid(true)
    } else setValid(false)
  }

  const handleIntervalChange = (newValues) => {
    let newProps = {
      ...intervalForm, 
      ...newValues
    };

    const isValid = IntervalSchema.isValidSync(newProps)
    if (isValid) {
      setIntervalForm(newProps)
      setValid(true)
    } else setValid(false)
  }

  const handleSubmit = async () => {
    const { title, message } = editorProps
    if (title === undefined || message === undefined || !valid) {
      toast.error(
        <Toast
          type="error"
          title="Campos em falta"
          description="titulo ou mensagem"
        />
      );
      return;
    }
    if (tab === types.all) {
      try {
        await sendEmailAll({ title, message })
        toast.success(
          <Toast
            type="success"
            title="Mensagem Enviada"
          />
        );
      } catch (err) {
        toast.error(
          <Toast
            type="error"
            title="Erro a Enviar mensagem"
            description={err.message}
          />
        );
      }
    }
    if (tab === types.some) {
      const { to } = editorProps
      const dirty = {
        subweb: to, subject: title
      };
      if (to === undefined || to.length === 0) {
        toast.error(
          <Toast
            type="error"
            title="Campos em falta"
            description="destinatário"
          />
        );
        return;
      }
      const params = cleanDeep(dirty)
      const query = queryString.stringify(params)
      try {
        await sendEmailSome({ message, query })
        toast.success(
          <Toast
            type="success"
            title="Mensagem Enviada"
          />
        );
      } catch (err) {
        toast.error(
          <Toast
            type="error"
            title="Erro a Enviar mensagem"
            description={err.message}
          />
        );
      }
    }
    if (tab === types.admin) {
      try {
        await sendEmailAdmin({ title, message })
        toast.success(
          <Toast
            type="success"
            title="Mensagem Enviada"
          />
        );
      } catch (err) {
        toast.error(
          <Toast
            type="error"
            title="Erro a Enviar mensagem"
            description={err.message}
          />
        );
      }
    }
  }

  const handleExportPivots = async () => {
    try {
      const res = await getPivotsExcel()
      exportExcel(res, 'pivots')

      toast.success(
        <Toast
          type="success"
          title="Exportação de pivots bem sucedida"
        />
      );
    } catch (err) {
      toast.error(
        <Toast
          type="error"
          title="Erro a exportar pivots"
          description={err.message}
        />
      );
    }
    finally {
      setLoadingPivots(false)
    }
  }

  const handleExportStats = async () => {
    try {
      const res = await getStatsExcel(intervalForm)
      exportExcel(res, 'stats')

      toast.success(
        <Toast
          type="success"
          title="Exportação de estatísticas bem sucedida"
        />
      );
    } catch (err) {
      toast.error(
        <Toast
          type="error"
          title="Erro a exportar estatísticas"
          description={err.message}
        />
      );
    } finally {
      setLoadingStats(false)
    }
  }

  const renderSubForm = () => {
    switch (tab) {
    case types.all: {
      return (
        <>
          <Spacer bottom="s" top="s">
            <Text modifiers={['weightBase']}>Enviar para:</Text>
            <Spacer bottom="s" top="l">
              <Text modifiers={['light']}>Todos os Pivots</Text>
            </Spacer>
          </Spacer>
          <Field
            title="Assunto"
            name="title"
            component={FieldCustom}
          />
          <Field
            title="Mensagem"
            name="message"
            component={FieldMarkdown}
          />
        </>
      )
    }
    case types.some: {
      return (
        <>
          <Spacer top="s" />
          <Field
            title="Enviar para:"
            name="to"
            options={userSubwebs}
            component={FieldSelect}
            placeholder="Selecione as subwebs..."
            isMulti
          />
          <Field
            title="Assunto"
            name="title"
            component={FieldCustom}
          />
          <Field
            title="Mensagem"
            name="message"
            component={FieldMarkdown}
          />
        </>

      )
    }
    default: {
      return (

        <Form>
          <Spacer bottom="s" top="s">
            <Text modifiers={['weightBase']}>Enviar para:</Text>
            <Spacer bottom="s" top="l">
              <Text modifiers={['light']}>Administradores/gestores</Text>
            </Spacer>
          </Spacer>
          <Field
            title="Assunto"
            name="title"
            component={FieldCustom}
          />
          <Field
            title="Mensagem"
            name="message"
            component={FieldMarkdown}
          />
        </Form>

      )
    }
    }
  }

  const renderForm = (props) => (
    <Form>
      <Spacer top="xl">
        <Field
          title="Enviar email para:"
          name="destination"
          options={emailOptions}
          onChange={(option, p) => {
            props.setFieldValue('destination', option.value)
            setTab(option.value);
          }}
          component={FieldSelect}
          placeholder="Selecione os destinatários"
        />
      </Spacer>
      {renderSubForm()}
    </Form>
  )

  const renderIntervalsForm = (props) => (
    <Form>
      <Spacer top="xl"> 
        <FormSpacer>
          <Field
            title="Data de inicio"
            name="startDate"
            component={FieldDate}
            type="datetime-local"
          />
          <Field
            title="Data de fim"
            name="endDate"
            component={FieldDate}
            type="datetime-local"
          />
        </FormSpacer>
      </Spacer>
    </Form>
  )
  
  const renderTab = () => {
    switch (func) {
    case functionalities.emails: {
      return (
        <Spacer bottom="xl2">
          <Formik
            initialValues={initialProps}
            validationSchema={Schema}
            validate={handleFormChange}
            onSubmit={(values, { setSubmitting }) => {
              handleSubmit(values);
              setSubmitting(false);
            }}
          > 
            {
              props => (
                renderForm(props) 
              )
            }
          </Formik>
          <ButtonWrapper>
            <Button modifiers={['dark', 'primaryHover']} onClick={() => handleSubmit()} type="submit">
              Enviar
            </Button>
          </ButtonWrapper>
        </Spacer>
      )
    }
    case functionalities.exports: {
      return (
        <GlobalActions>
          <FeatWrapper>
            <Tooltip tooltip="Exportar pivots" placement="right-end" hideArrow>
              <ButtonMenu
                icon="fal fa-file-export"
                modifiers={computedButtonModifiers}
                onClick={() => {
                  setLoadingPivots(true)
                  handleExportPivots()
                }}
              >
                Exportar pivots {loadingPivots && <LoaderSpinner />}
              </ButtonMenu>
            </Tooltip>
          </FeatWrapper>
          <Formik
            initialValues={intervalForm}
            validationSchema={IntervalSchema}
            validate={handleIntervalChange}
            onSubmit={(values, { setSubmitting }) => {
              setSubmitting(false);
            }}
          >
            {
              props => (
                renderIntervalsForm(props)
              )
            }
          </Formik>
          <Tooltip tooltip="Exportar estatísticas" placement="right-end" hideArrow>
            <ButtonMenu
              icon="fal fa-file-export"
              modifiers={computedButtonModifiers}
              onClick={() => {
                setLoadingStats(true)
                handleExportStats()
              }}
            >
              Exportar dados {loadingStats && <LoaderSpinner />}
            </ButtonMenu>
          </Tooltip>
        </GlobalActions>
      )
    }
    default: {
      return (
        <>
          <Formik
            initialValues={initialProps}
            validationSchema={Schema}
            validate={handleFormChange}
            onSubmit={(values, { setSubmitting }) => {
              handleSubmit(values);
              setSubmitting(false);
            }}
          >
            {
              props => (
                renderForm(props) 
              )
            }
          </Formik>
          <ButtonWrapper>
            <Button modifiers={['dark', 'primaryHover']} onClick={() => handleSubmit()} type="submit">
              Enviar
            </Button>
          </ButtonWrapper>
        </>
      )
    }
    }
  }

  return (
    <Layout>
      <Home>
        <Top>
          <Home.Header>
            <Title>Ferramentas de Administração</Title>
          </Home.Header>
        </Top>
        <Spacer bottom="xl2">
          <TabsWrapper>
            <TabsLineList tabs={newTabs} modifiers={['stretch', 'primary']} />
          </TabsWrapper>
          {renderTab()}
        </Spacer>
      </Home>
    </Layout>
  )
}

const FormSpacer = styled.div`
  display: flex;
  gap: 16px;
  padding: 0.75rem;
`

const Top = styled.div`
  display: flex;
  justify-content: space-between;
`

const ButtonWrapper = styled.div`
  display:flex;
  width: 100%;
  justify-content: flex-end;
`

const GlobalActions = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  height: fit-content;
  
  a {
    text-decoration: none !important;
  }

  button {
    display:flex;
    gap: 6px;
  }
`

const Home = styled.div`
  ${HelperTitle} {
    margin-bottom: ${props => props.theme.spacing.l};
  }
  ${props => props.theme.mixins.select()};
`;

Home.Header = styled.div`
  margin-bottom: ${props => props.theme.spacing.l};
`;

const TabsWrapper = styled.div`
  border-bottom: 1px solid rgba(238, 238, 238, 0.933);
`

const FeatWrapper = styled.div`
  padding: 1.875rem 0;
  border-bottom: 1px solid rgba(238, 238, 238, 0.933);
`

Page.propTypes = {
  initialProps: PropTypes.shape({
    title: PropTypes.string,
    message: PropTypes.string,
    to: PropTypes.array,
    destination: PropTypes.string
  }),
};

Page.defaultProps = {
  initialProps: {
    title: '',
    message: '',
    to: [],
    destination: types.admin
  }
};
export default withTheme(Page);
